/**
 * The report's section-visibility gates, as pure functions.
 *
 * MIRRORS the gates on the sections array inside ReportNext; the two are meant
 * to change together, and the a11y + surface tests assert they agree. This
 * lives outside `ReportNext.tsx` so the command palette can derive its
 * in-report jump commands WITHOUT a static edge into the report and everything
 * the report renders: the shell stays eager, the report is a lazy chunk.
 */
import { parseAutocrypt } from "../derivations/autocrypt";
import type { EmailAnalysis } from "../email-analysis";
import type { ParsedEmail } from "../email-parser";

/** True when the message body carried any parsed machine report. */
export function hasReports(analysis: EmailAnalysis): boolean {
  const r = analysis.layers.reports;
  return Boolean(r.dmarcAggregate || r.tlsrpt || r.mdn || r.arf || r.dsn);
}

/** The section ids this report will render, in order. */
export function visibleReportSectionIds(
  parsed: ParsedEmail,
  analysis: EmailAnalysis,
): string[] {
  const local = analysis.local;
  const htmlRiskTotal =
    local.htmlRisks.formCount +
    local.htmlRisks.iframeCount +
    local.htmlRisks.objectEmbedCount +
    local.htmlRisks.metaRefreshCount +
    local.htmlRisks.inlineEventHandlerCount;
  const ids: (string | false | null | undefined)[] = [
    "envelope",
    hasReports(analysis) && "reports",
    "auth",
    analysis.layers.security.spamScores.length > 0 && "reputation",
    "route",
    "links",
    "attachments",
    local.calendar && "calendar",
    analysis.indicators.length > 0 && "iocs",
    htmlRiskTotal > 0 && "htmlrisks",
    local.promptInjection.totalCount > 0 && "injection",
    local.unicodeIssues.length > 0 && "unicode",
    "body",
    "mime",
    "headers",
    (analysis.securityParts.length > 0 || parseAutocrypt(parsed.headers) != null) &&
      "crypto",
    analysis.sandbox && "sandbox",
    "threading",
    analysis.defects.length + parsed.defects.length > 0 && "defects",
    "raw",
  ];
  return ids.filter((x): x is string => typeof x === "string");
}
